import { Method } from 'axios';
import config from 'config/config';
import {
    BlockedLocation,
    BookingSwapResult,
    BookingUpdatePayload,
    LanBooking,
    LanRegistration,
    LanSettingsPayload,
    RegistrationUpdatePayload
} from 'types/intra';
import { createAxiosInstance } from 'utils/axiosConfig';
import { getMockBlockedLocations, getMockBookings, getMockLanSettings, getMockRegistrations } from './intraServiceMocks';

export interface SteamUserResponse {
    steamId: string;
    personaName: string;
    avatarUrl: string;
}

class IntraService {
    host = config.API_BASE_URL;
    apiPath = '/v1/intra';
    private useMocks = config.USE_MOCK_API === 'true';
    private axiosInstance = createAxiosInstance();

    private mockRegistrations: LanRegistration[] = getMockRegistrations();
    private mockBookings: LanBooking[] = getMockBookings();
    private mockLanSettings: LanSettingsPayload = getMockLanSettings();
    private mockBlockedLocations: BlockedLocation[] = getMockBlockedLocations();

    private async simulate<T>(payload: T, delay = 400): Promise<T> {
        return new Promise(resolve => {
            setTimeout(() => resolve(payload), delay);
        });
    }

    private async request<T>(path: string, method: Method, token: string | undefined, data?: unknown, basePath?: string): Promise<T> {
        if (!token) {
            throw new Error('Authentication token puuttuu. Kirjaudu sisään uudelleen.');
        }
        const apiBase = basePath ?? this.apiPath;
        const url = `${this.host}${apiBase}${path}`;
        const response = await this.axiosInstance({
            url,
            method,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            data
        });
        if (response.status === 204 || response.data == null) {
            return undefined as T;
        }
        const payload = response.data as { data: T };
        return payload.data;
    }

    async fetchRegistrations(token?: string): Promise<LanRegistration[]> {
        if (this.useMocks) {
            return this.simulate(this.mockRegistrations.map(item => ({ ...item })));
        }
        return this.request<LanRegistration[]>('/registrations', 'get', token);
    }

    async updateRegistration(id: string, payload: RegistrationUpdatePayload, token?: string): Promise<LanRegistration> {
        if (this.useMocks) {
            this.mockRegistrations = this.mockRegistrations.map(registration =>
                registration.id === id ? { ...registration, ...payload } : registration
            );
            const updated = this.mockRegistrations.find(registration => registration.id === id);
            if (!updated) {
                throw new Error('Registration not found');
            }
            return this.simulate({ ...updated });
        }
        return this.request<LanRegistration>(`/registrations/${id}`, 'put', token, payload);
    }

    async fetchBookings(token?: string): Promise<LanBooking[]> {
        if (this.useMocks) {
            return this.simulate(this.mockBookings.map(item => ({ ...item })));
        }
        return this.request<LanBooking[]>('/bookings', 'get', token);
    }

    async updateBooking(id: string, payload: BookingUpdatePayload, token?: string): Promise<LanBooking> {
        if (this.useMocks) {
            this.mockBookings = this.mockBookings.map(booking =>
                booking.id === id ? { ...booking, ...payload } : booking
            );
            const updated = this.mockBookings.find(booking => booking.id === id);
            if (!updated) {
                throw new Error('Booking not found');
            }
            return this.simulate({ ...updated });
        }
        return this.request<LanBooking>(`/bookings/${id}`, 'put', token, payload);
    }

    async swapBookingLocations(id: string, location: string, token?: string): Promise<BookingSwapResult> {
        if (this.useMocks) {
            const source = this.mockBookings.find(booking => booking.id === id);
            if (!source) {
                throw new Error('Booking not found');
            }
            const occupant = this.mockBookings.find(
                booking => booking.id !== id && booking.location === location
            );
            const sourceOld = source.location ?? null;
            this.mockBookings = this.mockBookings.map(booking => {
                if (booking.id === id) {
                    return { ...booking, location };
                }
                if (occupant && booking.id === occupant.id) {
                    return { ...booking, location: sourceOld };
                }
                return booking;
            });
            return this.simulate({
                source: { ...this.mockBookings.find(booking => booking.id === id)! },
                target: occupant ? { ...this.mockBookings.find(booking => booking.id === occupant.id)! } : null
            });
        }
        return this.request<BookingSwapResult>(`/bookings/${id}/swap`, 'post', token, { location });
    }

    async updateBillingStatus(id: string, done: number | boolean, token?: string): Promise<LanBooking> {
        if (this.useMocks) {
            return this.updateBooking(id, { done }, token);
        }
        return this.request<LanBooking>(`/bookings/${id}/billing`, 'patch', token, { done });
    }

    async fetchBlockedLocations(token?: string): Promise<BlockedLocation[]> {
        if (this.useMocks) {
            return this.simulate(this.mockBlockedLocations.map(item => ({ ...item })));
        }
        return this.request<BlockedLocation[]>('/blocked-locations', 'get', token);
    }

    async blockLocation(location: string, token?: string): Promise<BlockedLocation> {
        if (this.useMocks) {
            const existing = this.mockBlockedLocations.find(item => item.location === location);
            if (existing) {
                return this.simulate({ ...existing });
            }
            const created: BlockedLocation = {
                id: `blocked-${Date.now()}`,
                location,
                createdAt: new Date().toISOString()
            };
            this.mockBlockedLocations = [...this.mockBlockedLocations, created];
            return this.simulate({ ...created });
        }
        return this.request<BlockedLocation>('/blocked-locations', 'post', token, { location });
    }

    async unblockLocation(location: string, token?: string): Promise<void> {
        if (this.useMocks) {
            this.mockBlockedLocations = this.mockBlockedLocations.filter(item => item.location !== location);
            return this.simulate(undefined);
        }
        await this.request<void>(`/blocked-locations/${encodeURIComponent(location)}`, 'delete', token);
    }

    async fetchLanSettings(token?: string): Promise<LanSettingsPayload | null> {
        if (this.useMocks) {
            return this.simulate({ ...this.mockLanSettings });
        }
        return this.request<LanSettingsPayload | null>('/settings/current', 'get', token);
    }

    async updateLanSettings(payload: LanSettingsPayload, token?: string): Promise<LanSettingsPayload> {
        if (this.useMocks) {
            this.mockLanSettings = { ...this.mockLanSettings, ...payload, updatedAt: new Date().toISOString() };
            return this.simulate({ ...this.mockLanSettings });
        }
        if (payload.id) {
            return this.request<LanSettingsPayload>(`/settings/${payload.id}`, 'put', token, payload);
        }
        return this.request<LanSettingsPayload>('/settings', 'post', token, payload);
    }

    async fetchSteamUser(steamId: string, token?: string): Promise<SteamUserResponse | null> {
        if (!steamId) {
            return null;
        }
        if (this.useMocks) {
            // Return mock Steam user data for testing
            return this.simulate({
                steamId,
                personaName: 'MockPlayer',
                avatarUrl: `https://avatars.steamstatic.com/fef49e7fa7e1997310d705b2a6158ff8dc1cdfeb_medium.jpg`
            });
        }
        if (!token) {
            return null;
        }
        try {
            return await this.request<SteamUserResponse>(`/user/${encodeURIComponent(steamId)}`, 'get', token, undefined, '/v1/steam');
        } catch {
            // Silently ignore lookup errors
            return null;
        }
    }
}

export default new IntraService();
